import { Box, IconButton, Tooltip } from '@mui/material';
import { Button } from 'src/components/new/Button';
import { Spacer } from 'src/components/new/Spacer';
import { Typography } from 'src/components/new/Typography';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import BkTable from 'src/components/table/BkTable';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useEffect, useState } from 'react';
import AddDescription from './modals/AddDescription';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import {
  createDocumentDescription,
  deleteDocumentDescription,
  editDocumentDescription,
  getDocumentDescriptions,
} from 'src/features/company/utils';
import { handleDescriptionNameChange, updateDescriptions } from 'src/features/company/companySlice';

const columns = [
  {
    headerName: 'Reorder',
    field: 'reorder',
    alignSelf: 'center',
    headerCustomStyles: { width: '10%', textAlign: 'center' },
  },
  { headerName: 'Document Description', field: 'description', headerCustomStyles: { width: '80%' } },
  { headerName: 'Action', field: 'action' },
];

const DocumentDescriptions: React.FC = () => {
  const [snackBarState, setSnackbarState] = useState<{
    message: string;
    isOpened: boolean;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    message: '',
    isOpened: false,
    severity: 'error',
  });

  const [data, setData] = useState<any[]>([]);
  const [isAddDescriptionOpen, setIsAddDescriptionOpen] = useState(false);
  const [descriptionIdToEdit, setDescriptionIdToEdit] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  const dispatch: AppDispatch = useDispatch();

  const documentDescriptions = useSelector((state: RootState) => state.company.documentDescriptions);
  const isEditing =
    descriptionIdToEdit !== null && documentDescriptions.map((desc) => desc.id).includes(descriptionIdToEdit);

  const orgRole = useSelector((state: RootState) => state.user.currentUser.role);

  const isOrgAdmin = orgRole === 'admin';

  useEffect(() => {
      dispatch(getDocumentDescriptions());
  }, []);

  useEffect(() => {
    let data = [];
    for (let description of documentDescriptions) {
      data.push({
        id: description.id,
        reorder: (
          <IconButton
            disableRipple
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            disabled={!isOrgAdmin}
          >
            <DragIndicatorIcon sx={{ cursor: 'move' }} />
          </IconButton>
        ),
        description: description.text,
        action: renderAction(description),
      });
    }
    setData(data);
  }, [documentDescriptions]);

  const renderAction = (description: { text: string; id: number }) => {
    return (
      <Tooltip
        disableHoverListener={isOrgAdmin}
        disableFocusListener={isOrgAdmin}
        disableTouchListener={isOrgAdmin}
        title="Only admins can edit or remove descriptions"
      >
        <Box sx={{ justifyContent: 'center', display: 'flex', gap: 2 }}>
          <IconButton disabled={!isOrgAdmin} onClick={() => onEditClick(description)}>
            <CreateOutlinedIcon />
          </IconButton>
          <IconButton disabled={!isOrgAdmin} onClick={() => onDeleteDescription(description.id)}>
            <DeleteOutlineOutlinedIcon />
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  const onEditClick = (description: { id: number; text: string }) => {
    dispatch(handleDescriptionNameChange(description.text));
    setDescriptionIdToEdit(description.id as any);
    setIsAddDescriptionOpen(true);
  };

  const onAddClick = () => {
    setIsAddDescriptionOpen(true);
  };

  const onDeleteDescription = (id: number) => {
    dispatch(deleteDocumentDescription(id)).then((result) => {
      if (deleteDocumentDescription.fulfilled.match(result)) {
        const newDescriptions = documentDescriptions.filter((description) => description.id !== id);
        dispatch(updateDescriptions(newDescriptions));
        setSnackbarState({
          isOpened: true,
          message: 'Successfully deleted description',
          severity: 'success',
        });
      }

      if (deleteDocumentDescription.rejected.match(result)) {
        setSnackbarState({
          isOpened: true,
          message: 'Something went wrong while deleting description',
          severity: 'error',
        });
      }
    });
  };

  const onCreateDescription = (description: string) => {
    dispatch(
      createDocumentDescription({
        text: description,
        orderNum:
          documentDescriptions.length > 0 ? documentDescriptions[documentDescriptions.length - 1].orderNum + 1 : 1,
      }),
    ).then((result) => {
      if (createDocumentDescription.fulfilled.match(result)) {
        dispatch(updateDescriptions([...documentDescriptions, result.payload[0]]));
        setSnackbarState({
          isOpened: true,
          message: `Successfully added ${description} to the list`,
          severity: 'success',
        });
      }

      if (createDocumentDescription.rejected.match(result)) {
        setSnackbarState({
          isOpened: true,
          message: `Something went wrong while creating description`,
          severity: 'error',
        });
      }
    });
  };

  const onEditDescription = (description: string) => {
    dispatch(editDocumentDescription({ id: descriptionIdToEdit as number, text: description })).then((result) => {
      if (editDocumentDescription.fulfilled.match(result)) {
        const modifiedDescriptions = documentDescriptions.map((desc) =>
          desc.id === descriptionIdToEdit ? { ...desc, text: description } : desc,
        );
        dispatch(updateDescriptions([...modifiedDescriptions]));
        setSnackbarState({
          isOpened: true,
          message: 'Successfully modified description',
          severity: 'success',
        });
      }

      if (editDocumentDescription.rejected.match(result)) {
        setSnackbarState({
          isOpened: true,
          message: 'Something went wrong while modifying description',
          severity: 'error',
        });
      }
    });
  };

  const onModalClose = () => {
    setIsAddDescriptionOpen(false);
    dispatch(handleDescriptionNameChange(''));
    setDescriptionIdToEdit(null);
  };

  const onAddEditDescriptionSuccess = (description: string) => {
    if (isEditing) {
      onEditDescription(description);
    } else {
      onCreateDescription(description);
    }
  };

  const handleReorder = (descriptionInfo: { dragIndex: number; dropIndex: number; text: string; id: number }) => {
    const desc = [...documentDescriptions];
    const mappedDescriptions = desc.map((description) => {
      if (descriptionInfo.id === description.id) {
        return { ...description, orderNum: descriptionInfo.dropIndex + 1 };
      }
      if (
        descriptionInfo.dragIndex > descriptionInfo.dropIndex &&
        description.orderNum >= descriptionInfo.dropIndex + 1 &&
        description.orderNum < descriptionInfo.dragIndex + 1
      ) {
        return { ...description, orderNum: description.orderNum + 1 };
      }
      if (
        descriptionInfo.dragIndex < descriptionInfo.dropIndex &&
        description.orderNum > descriptionInfo.dragIndex + 1 &&
        description.orderNum <= descriptionInfo.dropIndex + 1
      ) {
        return { ...description, orderNum: description.orderNum - 1 };
      }
      return description;
    });
    const updatedData = mappedDescriptions.map((description) => ({
      id: description.id,
      orderNum: description.orderNum,
    }));
    dispatch(editDocumentDescription(updatedData)).then((result) => {
      if (editDocumentDescription.fulfilled.match(result)) {
        dispatch(updateDescriptions(mappedDescriptions.sort((a, b) => a.orderNum - b.orderNum)));
        setSnackbarState({
          isOpened: true,
          message: 'Reorder successful',
          severity: 'success',
        });
      }

      if (editDocumentDescription.rejected.match(result)) {
        setSnackbarState({
          isOpened: true,
          message: 'Something went wrong while reordering descriptions',
          severity: 'error',
        });
      }
    });
  };

  return (
    <Box sx={{ padding: '2.5rem 1.5rem' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
        <Typography sx={{ fontSize: '2rem', lineHeight: '2.5rem' }} fontWeight={400}>
          Document Description
        </Typography>
        <Button sx={{ width: '18.75rem' }} type="outline" onClick={onAddClick}>
          <AddOutlinedIcon />
          <Spacer w={8} />
          Add Description
        </Button>
      </Box>
      <BkTable
        reorderable={isOrgAdmin}
        columns={columns as any}
        data={showAll ? data : data.slice(0, 4) || []}
        initialRowsPerPage={50}
        renderPagination={false}
        onReorder={isOrgAdmin ? handleReorder : undefined}
      />
      {!showAll && data.length > 4 && (
        <Button type="outline" onClick={() => setShowAll(true)} sx={{ width: '18.75rem', mt: 2 }}>
          Show More
        </Button>
      )}
      <AddDescription
        open={isAddDescriptionOpen}
        mode={isEditing ? 'Edit' : 'Add'}
        onSuccess={onAddEditDescriptionSuccess}
        onClose={onModalClose}
      />
      <BkSnackBar
        open={snackBarState.isOpened}
        handleCloseSnackbar={() => setSnackbarState({ ...snackBarState, isOpened: false })}
        snackMessage={snackBarState.message}
        severity={snackBarState.severity}
      />
    </Box>
  );
};

export default DocumentDescriptions;
